//* Libraries imports
import { useQuery } from "@tanstack/react-query";

//* Schemas Import
import { paginatedResourceQuestion } from "../../../schemas/common/resource/question/resource-question";

//* Pagination schemas
import { laravelPaginationMetaSchema } from "../../../schemas/common/paginated-data";

//* Axios Import
import { privateApi } from "../../../services/apis/configs/apiClient";

//* Zod Import
import { z } from "zod";

//* Interface to define pagination parameters
type GetAllParams = {
  page?: number;
  perPage?: number;
  search?: string;
}

const resourceQuestionResponseSchema = z.object({
  recursos: z.array(paginatedResourceQuestion),
  pagination: laravelPaginationMetaSchema,
});

/**
 * API for resource questions  
 */
const ResourceQuestionAPI = {
  /**
   * Gets all question resources with pagination support
   * @param params Pagination parameters (page, items per page, search term)
   * @returns Paginated list of question resources
   */
  getAll: async function (params?: GetAllParams) {
    try {
      const response = await privateApi.get(
        "/student_portal/private/recursos/questoes",
        {
          params: {
            page: params?.page || 1,
            limit: params?.perPage || 10,
            search: params?.search || "",
          },
        }
      );

      const validatedResponse = resourceQuestionResponseSchema.parse(response.data);
      
      return {
        data: validatedResponse.recursos,
        meta: validatedResponse.pagination,
        success: true
      };
    } catch (error) {
      console.error("Error fetching question resources:", error);
      return {
        data: [],
        success: false,
        message: "Unable to load question resources. Please try again later."
      };
    }
  }
}

/**
 * Hook to get paginated question resources
 * @param page Current page number
 * @param perPage Number of items per page
 * @param search Search term (optional) - minimum 3 characters to activate search
 * @returns Query for paginated question resources
 */
export function useGetPaginatedResourceQuestion(
  page: number = 1,
  perPage: number = 10,
  search: string = ""
) {
  const shouldSearch = search.length === 0 || search.length >= 3;
  
  return useQuery({
    queryKey: ["paginated-resource-question", page, perPage, search],
    queryFn: async () =>
      await ResourceQuestionAPI.getAll({
        page,
        perPage,
        search: shouldSearch ? search : "",
      }),
    enabled: shouldSearch,
    retry: 3,
    staleTime: 1000 * 60 * 1,
    gcTime: 1000 * 60 * 10,
  });
}
